// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/FichasTecnicas.tsx
//  Ficha Técnica / BOM versionada (paridade desktop FrmCadFichaTecnica).
//  Cadastro de fórmulas de produção (produto acabado + insumos) com histórico de versões.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, BookOpen, Trash2, Save, Eye, History, Pencil, Printer } from 'lucide-react';

import {
  fichaTecnicaService,
  type FichaTecnicaResumo,
  type FichaTecnicaItemRequest,
  type FichaTecnicaDetalhe,
} from '@/features/operacional/services/fichaTecnicaService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { ProdutoNome } from '@/components/ProdutoNome';
import { BarraFiltros } from '@/components/BarraFiltros';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const brl = (v: number) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtQtd = (v: number) => (v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

const fmtDataHora = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('pt-BR');
};

interface ItemState extends FichaTecnicaItemRequest {
  _key: number;
}

// ── Página ──────────────────────────────────────────────────────────────────

export default function FichasTecnicas() {
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen]     = useState(false);
  const [editId, setEditId]         = useState<number | null>(null);
  const [detalheId, setDetalheId]   = useState<number | null>(null);

  // ── Estado do formulário ──────────────────────────────────────────────
  const [produtoId, setProdutoId]     = useState<number | undefined>(undefined);
  const [descricao, setDescricao]     = useState('');
  const [referencia, setReferencia]   = useState('');
  const [quantidade, setQuantidade]   = useState(1);
  const [perda, setPerda]             = useState(0);
  const [observacao, setObservacao]   = useState('');
  const [itens, setItens]             = useState<ItemState[]>([]);
  const [nextKey, setNextKey]         = useState(1);

  // ── Filtros + paginação server-side (antes: 100 fixas, sem busca) ───────
  const [busca, setBusca]                 = useState('');
  const [filtroProdutoId, setFiltroProdutoId] = useState<number | undefined>(undefined);
  const [situacao, setSituacao]           = useState<'todas' | 'ativas' | 'inativas'>('todas');
  const [removendoId, setRemovendoId]     = useState<number | null>(null);
  const filtrosAtivos = (busca ? 1 : 0) + (filtroProdutoId ? 1 : 0) + (situacao !== 'todas' ? 1 : 0);
  const pag = usePaginacaoServidor({ resetarQuando: [busca, filtroProdutoId, situacao] });

  const { data, isLoading } = useQuery({
    queryKey: ['fichas-tecnicas', pag.pagina, pag.tamanhoPagina, busca, filtroProdutoId, situacao],
    queryFn: () => fichaTecnicaService.getAll(pag.pagina, pag.tamanhoPagina, {
      busca, produtoId: filtroProdutoId, situacao: situacao === 'todas' ? undefined : situacao,
    }),
    placeholderData: (anterior) => anterior,
  });
  const fichas = data?.itens ?? [];

  const resetForm = () => {
    setProdutoId(undefined); setDescricao(''); setReferencia('');
    setQuantidade(1); setPerda(0); setObservacao(''); setItens([]); setNextKey(1);
    setEditId(null);
  };

  const addItem = () =>
    setNextKey((k) => {
      setItens((p) => [...p, { _key: k, materiaPrimaId: 0, quantidade: 1, custoUnitario: 0 }]);
      return k + 1;
    });
  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));
  const updItem = (key: number, field: keyof FichaTecnicaItemRequest, value: number) =>
    setItens((p) => p.map((r) => (r._key === key ? { ...r, [field]: value } : r)));

  const abrirNovo = () => { resetForm(); setFormOpen(true); };

  const abrirEdicao = async (id: number) => {
    try {
      const f = await fichaTecnicaService.getById(id);
      setEditId(f.id);
      setProdutoId(f.produtoId);
      setDescricao(f.descricao ?? '');
      setReferencia(f.referencia ?? '');
      setQuantidade(f.quantidade);
      setPerda(f.perTotalPerda);
      setObservacao(f.observacao ?? '');
      setItens(f.itens.map((it, idx) => ({
        _key: idx + 1,
        materiaPrimaId: it.materiaPrimaId,
        quantidade: it.quantidade,
        custoUnitario: it.custoUnitario,
      })));
      setNextKey(f.itens.length + 1);
      setFormOpen(true);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!produtoId) throw new Error('Selecione o produto acabado');
      const itensValidos = itens.filter((i) => i.materiaPrimaId > 0 && i.quantidade > 0);
      if (itensValidos.length === 0) throw new Error('Adicione ao menos um insumo');
      const payload = {
        produtoId,
        quantidade,
        descricao: descricao || undefined,
        referencia: referencia || undefined,
        perTotalPerda: perda,
        observacao: observacao || undefined,
        itens: itensValidos.map(({ materiaPrimaId, quantidade, custoUnitario }) => ({
          materiaPrimaId, quantidade, custoUnitario,
        })),
      };
      return editId ? fichaTecnicaService.update(editId, payload) : fichaTecnicaService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fichas-tecnicas'] });
      toast.success(editId ? 'Ficha técnica atualizada (nova versão registrada)' : 'Ficha técnica criada');
      setFormOpen(false);
      resetForm();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => fichaTecnicaService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fichas-tecnicas'] });
      toast.success('Ficha técnica removida');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const { data: detalhe } = useQuery({
    queryKey: ['ficha-tecnica-detalhe', detalheId],
    queryFn: () => fichaTecnicaService.getById(detalheId!),
    enabled: detalheId != null,
  });

  // Simulação: escala os insumos do lote para a quantidade a produzir (começa = lote).
  const [qtdProduzir, setQtdProduzir] = useState(0);
  useEffect(() => { if (detalhe) setQtdProduzir(detalhe.quantidade); }, [detalhe?.id, detalhe?.quantidade]);
  const escala = detalhe && detalhe.quantidade > 0 ? qtdProduzir / detalhe.quantidade : 1;

  /** Imprime a ficha (com a simulação) numa janela própria. Nomes vêm do cache do ProdutoNome. */
  const imprimirFicha = (f: FichaTecnicaDetalhe, qtd: number, fator: number) => {
    const nome = (id: number) =>
      queryClient.getQueryData<{ descricao?: string }>(['produto', id])?.descricao ?? '';
    const esc = (s: string) =>
      s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
    const linhas = f.itens.map((it) => `
      <tr><td>[${it.materiaPrimaId}] ${esc(nome(it.materiaPrimaId))}</td>
      <td class="n">${fmtQtd(it.quantidade)}</td><td class="n">${fmtQtd(it.quantidade * fator)}</td>
      <td class="n">${brl(it.custoTotal * fator)}</td><td class="n">${it.percentualComposicao}%</td></tr>`).join('');
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast.error('Permita pop-ups para imprimir a ficha'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Ficha Técnica</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px;margin:24px}h1{font-size:18px;margin:0 0 4px}
      table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #999;padding:4px 6px;text-align:left}
      .n{text-align:right}.meta{margin:2px 0}</style></head><body>
      <h1>Ficha Técnica${f.descricao ? ' — ' + esc(f.descricao) : ''}</h1>
      <p class="meta">Produto acabado: [${f.produtoId}] ${esc(nome(f.produtoId))}</p>
      ${f.referencia ? `<p class="meta">Referência: ${esc(f.referencia)}</p>` : ''}
      <p class="meta">Lote: ${fmtQtd(f.quantidade)} &nbsp;|&nbsp; Quantidade a produzir: <b>${fmtQtd(qtd)}</b>
        &nbsp;|&nbsp; Custo: <b>${brl(f.custoTotalProducao * fator)}</b> &nbsp;|&nbsp; Perda: ${f.perTotalPerda}%</p>
      <table><thead><tr><th>Matéria-prima</th><th class="n">Qtd. no lote</th><th class="n">Qtd. p/ produzir</th>
      <th class="n">Custo</th><th class="n">% Comp.</th></tr></thead><tbody>${linhas}</tbody></table>
      ${f.observacao ? `<p class="meta" style="margin-top:12px">Obs.: ${esc(f.observacao)}</p>` : ''}
      <p class="meta" style="margin-top:16px;color:#666">Impresso em ${new Date().toLocaleString('pt-BR')}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const custoFormulario = useMemo(
    () => itens.reduce((s, i) => s + (i.quantidade || 0) * (i.custoUnitario || 0), 0),
    [itens],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Ficha Técnica / BOM</h1>
            <p className="text-sm text-muted-foreground">
              Fórmulas de produção versionadas — produto acabado e seus insumos.
            </p>
          </div>
        </div>
        <Button className="gap-2" onClick={abrirNovo}>
          <Plus className="h-4 w-4" />Nova Ficha
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <BarraFiltros
            busca={busca}
            onBuscar={setBusca}
            placeholderBusca="Descrição, referência ou produto..."
            filtrosAtivos={filtrosAtivos}
            onLimpar={() => { setBusca(''); setFiltroProdutoId(undefined); setSituacao('todas'); }}
          >
            <div className="space-y-1 w-64">
              <Label className="text-xs text-muted-foreground">Produto acabado</Label>
              <ProdutoSearchCombobox
                key={filtroProdutoId ?? 'sem-produto'}
                value={filtroProdutoId}
                onChange={setFiltroProdutoId}
                placeholder="Todos os produtos"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Situação</Label>
              <Select value={situacao} onValueChange={(v) => setSituacao(v as typeof situacao)}>
                <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="ativas">Ativas</SelectItem>
                  <SelectItem value="inativas">Inativas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </BarraFiltros>

          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Carregando...</div>
          ) : fichas.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>{filtrosAtivos > 0 ? 'Nenhuma ficha técnica encontrada com estes filtros' : 'Nenhuma ficha técnica cadastrada'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto Acabado</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead className="text-right">Lote</TableHead>
                  <TableHead className="text-right">Insumos</TableHead>
                  <TableHead className="text-right">Custo Produção</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fichas.map((f: FichaTecnicaResumo) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">
                      <span className="text-muted-foreground">[{f.produtoId}]</span>{' '}
                      {f.produtoDescricao ?? 'Produto não encontrado'}
                    </TableCell>
                    <TableCell>{f.descricao ?? '—'}</TableCell>
                    <TableCell>{f.referencia ?? '—'}</TableCell>
                    <TableCell className="text-right">{f.quantidade}</TableCell>
                    <TableCell className="text-right">{f.totalItens}</TableCell>
                    <TableCell className="text-right">{brl(f.custoTotalProducao)}</TableCell>
                    <TableCell>
                      {f.isInativo
                        ? <Badge className="bg-red-500/10 text-red-500">Inativa</Badge>
                        : <Badge className="bg-green-500/10 text-green-500">Ativa</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDetalheId(f.id)} title="Ver detalhe / versões">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => abrirEdicao(f.id)} title="Editar">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemovendoId(f.id)} title="Remover">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <PaginacaoRodape {...pag.rodape(data)} />
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={removendoId != null}
        onOpenChange={(o) => { if (!o) setRemovendoId(null); }}
        onConfirm={() => { if (removendoId != null) removeMutation.mutate(removendoId); setRemovendoId(null); }}
        title="Remover ficha técnica"
        description="Tem certeza que deseja remover esta ficha técnica? O histórico de versões também será removido."
      />

      {/* ── Form (criar/editar) ──────────────────────────────────────────── */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar Ficha Técnica' : 'Nova Ficha Técnica'}</DialogTitle>
            <DialogDescription>
              Defina o produto acabado, a quantidade do lote de produção e os insumos. Cada
              salvamento registra uma nova versão no histórico.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Produto Acabado</Label>
              <ProdutoSearchCombobox
                value={produtoId}
                onChange={setProdutoId}
                placeholder="Selecione o produto acabado..."
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Nome da fórmula" />
            </div>
            <div className="space-y-2">
              <Label>Referência</Label>
              <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Código" />
            </div>
            <div className="space-y-2">
              <Label>Quantidade do Lote</Label>
              <Input type="number" min={0} step="any" value={quantidade}
                onChange={(e) => setQuantidade(Number(e.target.value))} />
              <p className="text-xs text-muted-foreground">
                Unidades produzidas por este lote (base do rateio de consumo).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Perda Total (%)</Label>
              <Input type="number" min={0} step="any" value={perda}
                onChange={(e) => setPerda(Number(e.target.value))} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Insumos / Matérias-primas</Label>
              <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
                <Plus className="h-4 w-4" />Adicionar Insumo
              </Button>
            </div>
            {itens.length === 0 ? (
              <p className="text-center py-6 text-sm text-muted-foreground border rounded-md">
                Nenhum insumo. Clique em "Adicionar Insumo".
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px]">Matéria-prima</TableHead>
                    <TableHead className="text-right">Qtd.</TableHead>
                    <TableHead className="text-right">Custo Unit.</TableHead>
                    <TableHead className="text-right">Custo Total</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((i) => (
                    <TableRow key={i._key}>
                      <TableCell>
                        <ProdutoSearchCombobox
                          value={i.materiaPrimaId || undefined}
                          onChange={(id) => updItem(i._key, 'materiaPrimaId', id)}
                          placeholder="Buscar insumo..."
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min={0} step="any" className="w-24 text-right ml-auto"
                          value={i.quantidade}
                          onChange={(e) => updItem(i._key, 'quantidade', Number(e.target.value))} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min={0} step="any" className="w-28 text-right ml-auto"
                          value={i.custoUnitario ?? 0}
                          onChange={(e) => updItem(i._key, 'custoUnitario', Number(e.target.value))} />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {brl((i.quantidade || 0) * (i.custoUnitario ?? 0))}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(i._key)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-medium text-muted-foreground">
                      Custo Total de Produção:
                    </TableCell>
                    <TableCell className="text-right font-bold">{brl(custoFormulario)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormOpen(false); resetForm(); }}>Cancelar</Button>
            <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4" />{saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detalhe + histórico de versões ───────────────────────────────── */}
      <Dialog open={detalheId != null} onOpenChange={(o) => { if (!o) setDetalheId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ficha Técnica{detalhe?.descricao ? ` — ${detalhe.descricao}` : ''}</DialogTitle>
            <DialogDescription>
              {detalhe && <>Produto acabado: <ProdutoNome id={detalhe.produtoId} />. </>}
              Insumos e histórico de versões.
            </DialogDescription>
          </DialogHeader>

          {detalhe && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-muted-foreground">Lote</p><p className="font-medium">{detalhe.quantidade}</p></div>
                <div><p className="text-muted-foreground">Custo Produção</p><p className="font-medium">{brl(detalhe.custoTotalProducao)}</p></div>
                <div><p className="text-muted-foreground">Custo Unitário</p><p className="font-medium">{brl(detalhe.custoTotalUnitario)}</p></div>
              </div>

              {/* Simulação (paridade FrmConsultaFichaTecnica: "Quantidade a produzir") */}
              <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Quantidade a produzir</Label>
                  <Input type="number" min={0} step="any" className="h-9 w-32 text-right"
                    value={qtdProduzir}
                    onChange={(e) => setQtdProduzir(Math.max(0, Number(e.target.value)))} />
                </div>
                <div className="text-sm">
                  <p className="text-muted-foreground">Custo da simulação</p>
                  <p className="font-semibold">{brl(detalhe.custoTotalProducao * escala)}</p>
                </div>
                <Button variant="outline" size="sm" className="gap-2 h-9 ml-auto"
                  onClick={() => imprimirFicha(detalhe, qtdProduzir, escala)}>
                  <Printer className="h-4 w-4" />Imprimir
                </Button>
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Insumos</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matéria-prima</TableHead>
                      <TableHead className="text-right">Qtd. no lote</TableHead>
                      <TableHead className="text-right">Qtd. p/ produzir</TableHead>
                      <TableHead className="text-right">Custo</TableHead>
                      <TableHead className="text-right">% Comp.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalhe.itens.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell><ProdutoNome id={it.materiaPrimaId} /></TableCell>
                        <TableCell className="text-right">{fmtQtd(it.quantidade)}</TableCell>
                        <TableCell className="text-right font-medium">{fmtQtd(it.quantidade * escala)}</TableCell>
                        <TableCell className="text-right">{brl(it.custoTotal * escala)}</TableCell>
                        <TableCell className="text-right">{it.percentualComposicao}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <p className="text-sm font-medium mb-1 flex items-center gap-1">
                  <History className="h-4 w-4" />Histórico de versões
                </p>
                {detalhe.historicos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem registros.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {detalhe.historicos.map((h) => (
                      <li key={h.id} className="flex justify-between border-b py-1">
                        <span>{h.descricao}</span>
                        <span className="text-muted-foreground whitespace-nowrap ml-2">{fmtDataHora(h.dtUltimaAtualizacao)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalheId(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
